import CloseIcon from '@mui/icons-material/Close';
import { TextField, IconButton, Popover, Stack, Typography } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { axiosInstance } from '../../../../assets/utils/axiosInstance';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import RESTAPIError from '../../../../interfaces/RESTAPIError';
import { RootState } from '../../../../utils/store';

import ConfirmDialog from '~/components/common/dialog/confirmDialog';


interface AddAttendeeProps {
  meetingId: number;
  value:  string;
  open: boolean;
  onClose: () => void;
  selectCaseManagerRef: HTMLDivElement | null;
  refresh: () => void;
}


const styles = {
  textfield: {
    bgColor: '',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
};


const AddAttendee = ({ meetingId, value, open, onClose,  selectCaseManagerRef, refresh }: AddAttendeeProps) => {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const tenantUserList = useSelector((state: RootState) => state.tenantUserList.items);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [dropdownAnchor, setDropdownAnchor] = useState<HTMLDivElement | null>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number>(0);
  const openDropdown = Boolean(dropdownAnchor);
  const id = open ? 'select-attendee-popover' : undefined;

  useEffect(() => {
    if (tenantUserList.length > 0)
      setIsDisabled(false);
  }, [tenantUserList]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDisabled) {
      setDropdownAnchor(event.currentTarget);
      setDropdownWidth(selectCaseManagerRef?.offsetWidth ?? 0);
    }
  };

  const handleClose = () => {
    setDropdownAnchor(null);
    onClose();
  };

  const UpdateMeetingAttendee = async () => {
    setIsLoading(true);
    try {
      const res = await axiosInstance().patch(`schedule/meetings/${meetingId}/update-case-handler`, {
        attendee_id: selectedUser,
      });
      if (res) {
        onClose();
        refresh();
      }

    } catch (err) {
      const apiError = err as RESTAPIError;
      if (apiError?.response?.status == `${StatusCodes.FORBIDDEN}`)
        createToastNotification(intl.formatMessage({ defaultMessage: 'You are not authorized to set Case handler for this meeting.' }), 'error');
      else
        createToastNotification(apiError.message, 'error');
    }
    handleClose();
    setOpenConfirmDialog(false);
    setIsLoading(false);
  };

  const openAssignmentHandler = (selectedId: number) => {
    setOpenConfirmDialog(true);
    setSelectedUser(selectedId);
  };

  const cancelAssignmentHandler = () => {
    setOpenConfirmDialog(false);
  };

  const confirmAssignmentHandler = () => {
    UpdateMeetingAttendee();
  };

  return (
    <>
      {open &&
        <TextField
          disabled={isDisabled}
          value={value}
          variant='standard'
          placeholder="Unassigned case manager"
          onClick={handleClick}
          InputProps={{
            disableUnderline: true,
            readOnly: true,
            endAdornment: (
              <IconButton  onClick={onClose}>
                <CloseIcon/>
              </IconButton>
            ),
          }}
          sx={styles.textfield}
        />
      }
      <Popover
        id={id}
        open={openDropdown}
        anchorEl={dropdownAnchor}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Stack sx={{
          minWidth: dropdownWidth !== 0 ? (dropdownWidth - 20) : '250px',
          paddingY: '10px',
          maxHeight: '400px',
        }}>
          {Array.isArray(tenantUserList) &&
            tenantUserList.map((user, key) => (
              <Typography
                key={key}
                sx={{
                  fontSize: '14px',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  '&:hover':{
                    bgcolor: '',
                  },
                }}
                onClick={() => openAssignmentHandler(user?.id)}
              >
                {user?.name ?? 'No Name'}
              </Typography>
            ))
          }
        </Stack>
      </Popover>
      {/* TODO: refactor to use `openConfirmationModal` instead of `ConfirmDialog` component */}
      <ConfirmDialog
        proceedButtonContent='Confirm'
        dialogContent='Please confirm case manager assignment.'
        dialogTitle='Assignment Confirmation'
        open={openConfirmDialog}
        handleCancel={cancelAssignmentHandler}
        handleConfirm={confirmAssignmentHandler}
        isLoading={isLoading}
      />
    </>
  );
};

export default AddAttendee;
